// Display formatting for wallet values.
//
// Amounts arrive from the BFF as decimal STRINGS and are formatted as strings.
// They are never parsed into a JS number on the way to the screen: a treasury
// balance can exceed what a double holds exactly, and a silently rounded
// balance is the kind of thing you discover during a reconciliation break.

/** "143750.5" → "143,750.5" — grouping only, digits untouched. */
export function groupDigits(decimal: string): string {
  const parts = String(decimal ?? '').split('.');
  const whole = parts[0] ?? '0';
  const frac = parts[1] ?? '';
  const sign = whole.startsWith('-') ? '-' : '';
  const digits = sign ? whole.slice(1) : whole;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}${grouped}${frac ? `.${frac}` : ''}`;
}

import { displayAsset } from './format';
import { KIND_WORDS } from './ledger-cable';

/** The register's class, as it is said and shown — pinned to contracts/wallet.mjs CLASS_WORDS by a test. */
export const CLASS_WORD: Record<string, string> = {
  deposit: 'Deposit',
  running_hot: 'Hot',
  dirty_hot: 'Merchant Hot',
  frozen: 'Flagged',
  treasury: 'Treasury',
  fee: 'Fee',
  exchange_deposit: 'Exchange deposit',
  customer_payout: 'Payout address',
};

/** The pipeline's pool, in the wallet's word. */
export const POOL_WORD: Record<string, string> = { clean: 'Hot', dirty: 'Merchant Hot', quarantine: 'Flagged' };

/** The network, spelled as a wallet's name spells it (docs/TERMINOLOGY.md §2: TRC20, never TRC or TRON). */
export function chainWord(chain: string | null | undefined): string {
  if (!chain) return '';
  return chain.toUpperCase() === 'BASE' ? 'Base' : chain.toUpperCase();
}

/** A wallet row's name: the register's one name from the server (N-091, "Hot · TRC20 · Nov"); from an older server
 *  that sends none, "<group> · <network>" — never a label typed by someone, never "Desk …". */
export function walletName(w: { class: string; chain?: string | null; name?: { full: string } | null }): string {
  if (w.name?.full) return w.name.full;
  return [CLASS_WORD[w.class] ?? w.class, chainWord(w.chain)].filter(Boolean).join(' · ');
}

/** "143750.5" + USDTTRC → "143,750.5 USDT·TRC" — the Books' asset word (lib/format.ts). */
export function money(amount: string, asset: string): string {
  return `${groupDigits(amount)} ${displayAsset(asset)}`;
}

/** Middle-truncate an address for dense rows. Never used where identity matters. */
export function shortAddress(addr: string, head = 6, tail = 6): string {
  if (!addr || addr.length <= head + tail + 1) return addr ?? '';
  // A tail of 0 shows the head only: the lists that name a destination the
  // operator may have to TYPE the ending of keep that ending off the screen
  // (audit of 21 Oct 2026, E3 — a shown ending passes the gate by habit).
  return `${addr.slice(0, head)}…${tail > 0 ? addr.slice(-tail) : ''}`;
}

/**
 * Time remaining, deliberately blunt. Approval windows are short and a card
 * that has quietly expired must look expired rather than merely old.
 */
export function timeLeft(iso: string, now = Date.now()): { text: string; urgent: boolean; expired: boolean } {
  const ms = new Date(iso).getTime() - now;
  if (Number.isNaN(ms)) return { text: '—', urgent: false, expired: false };
  if (ms <= 0) return { text: 'expired', urgent: false, expired: true };
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return { text: `${mins}m left`, urgent: mins <= 10, expired: false };
  const hrs = Math.floor(mins / 60);
  return { text: `${hrs}h ${mins % 60}m left`, urgent: false, expired: false };
}

function magnitude(ms: number): string {
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'moments';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

/**
 * Past-tense only. Handles a future timestamp explicitly rather than letting a
 * negative difference collapse to "just now" — a KYT expiry three weeks out
 * rendering as "expires just now" reads as an urgent problem that isn't real.
 */
export function relativeTime(iso: string, now = Date.now()): string {
  const ms = now - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  if (ms < 0) return `in ${magnitude(-ms)}`;
  const m = magnitude(ms);
  return m === 'moments' ? 'just now' : `${m} ago`;
}

/** Forward-looking: "in 20d", or "expired" once it has passed. */
export function until(iso: string, now = Date.now()): string {
  const ms = new Date(iso).getTime() - now;
  if (Number.isNaN(ms)) return '—';
  if (ms <= 0) return 'expired';
  return `in ${magnitude(ms)}`;
}

/**
 * Seconds-resolution countdown for a short-lived credential.
 *
 * timeLeft() is minute-granular, which is fine for a 12-hour approval window
 * and useless for a 15-minute signing link: it would sit on "1m left" for the
 * last sixty seconds and then jump to expired while the operator is still
 * walking to the safe.
 *
 * Never returns a negative or a rounded-up zero — "0:00 left" while the link
 * still worked, or "1m left" when it did not, are both worse than the truth.
 */
export function countdown(iso: string, now = Date.now()): { text: string; urgent: boolean; expired: boolean } {
  const ms = new Date(iso).getTime() - now;
  if (Number.isNaN(ms)) return { text: '—', urgent: false, expired: false };
  if (ms <= 0) return { text: 'expired', urgent: true, expired: true };

  const total = Math.floor(ms / 1000);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return {
    text: `${mins}:${String(secs).padStart(2, '0')} left`,
    // Two minutes is about how long the walk-and-tap takes; past that an
    // operator should start again rather than race it.
    urgent: ms <= 120_000,
    expired: false,
  };
}

// ---- amounts typed by the operator (the Pay / Move to Treasury sheets) ----

/** The most places USDT carries; the contract's toBaseUnits refuses more. */
export const AMOUNT_PLACES = 6;

/**
 * Why a typed amount cannot be sent, as a sentence — or null when it can.
 * Said here, before the server is asked, in the words the server's own
 * refusal (amount_invalid) would get; the server checks again with the
 * contract's conversion, so nothing rests on this alone.
 */
export function amountProblem(text: string): string | null {
  const t = String(text ?? '').trim();
  if (t === '') return 'Type the amount in USDT.';
  if (/,/.test(t)) return 'No commas — type the amount as plain digits, with a dot for the decimals.';
  if (!/^\d+(\.\d+)?$/.test(t)) return 'That is not an amount: plain digits, with a dot for the decimals.';
  const places = (t.split('.')[1] ?? '').length;
  if (places > AMOUNT_PLACES) return `USDT carries at most ${AMOUNT_PLACES} decimal places; this has ${places}.`;
  if (/^0+(\.0+)?$/.test(t)) return 'The amount must be above zero.';
  return null;
}

/** A card's kind, as it is said on the queue (the Approvals screen's words). */
// The signing page's words (cosign/src/verify.ts KIND_WORDS), so the sheet,
// the phone page and the list cannot disagree about what a move is called.
export const KIND_WORD: Record<string, string> = { ...KIND_WORDS };
